import React, { useState, useEffect, useCallback } from "react";
import { swalError, confirmDelete } from "../common/confirm";
import {
  Plus,
  X,
  AlertTriangle,
  Activity,
  Shield,
  TrendingUp,
  Zap,
  Trash2,
  Calendar,
  History,
} from "lucide-react";
import riskScoresService, { RiskScore } from "../../services/riskScoresService";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import linkService, {
  LinkedItem,
  RiskLinkType,
} from "../../services/linkService";
import ScoreRiskModal from "./ScoreRiskModal";
import RiskConfigModal from "./RiskConfigModal";
import { usePermissions } from "../../contexts/PermissionContext";
import ControlConfigModal from "../internalControls/ControlConfigModal";
import controlRegisterService, {
  ControlRegister,
} from "../../services/controlRegisterService";

// --- Components ---

// 1. The Surrounding List Nodes (Corner Rectangles)
type ThemeColor = "blue" | "green" | "amber" | "purple";

interface ListNodeProps {
  title: string;
  icon: any;
  color: ThemeColor;
  items: any[];
  onAdd: (text: string) => void;
  onRemove: (id: number) => void;
  placeholder: string;
  className?: string;
  /** When provided, "+" opens the parent's manage flow instead of inline text-add. */
  onManage?: () => void;
  /** Hides "+" and trash icons (derived data). */
  readOnly?: boolean;
  /** Optional subtitle shown under the count, e.g. "via controls". */
  subtitle?: string;
  /** When provided, clicking an item's label fires this with the item id. */
  onItemClick?: (id: number) => void;
}

const ListNode = ({
  title,
  icon: Icon,
  color,
  items,
  onAdd,
  onRemove,
  placeholder,
  className,
  onManage,
  readOnly,
  subtitle,
  onItemClick,
}: ListNodeProps) => {
  const [newItem, setNewItem] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (newItem.trim()) {
      onAdd(newItem);
      setNewItem("");
      setIsAdding(false);
    }
  };

  const theme = {
    blue: {
      border: "border-blue-200",
      bg: "bg-blue-50",
      headerBg: "bg-blue-100",
      text: "text-blue-900",
      icon: "text-blue-600",
      btn: "bg-blue-600 hover:bg-blue-700",
      ring: "focus:ring-blue-400",
    },
    green: {
      border: "border-emerald-200",
      bg: "bg-emerald-50",
      headerBg: "bg-emerald-100",
      text: "text-emerald-900",
      icon: "text-emerald-600",
      btn: "bg-emerald-600 hover:bg-emerald-700",
      ring: "focus:ring-emerald-400",
    },
    amber: {
      border: "border-amber-200",
      bg: "bg-amber-50",
      headerBg: "bg-amber-100",
      text: "text-amber-900",
      icon: "text-amber-600",
      btn: "bg-amber-600 hover:bg-amber-700",
      ring: "focus:ring-amber-400",
    },
    purple: {
      border: "border-purple-200",
      bg: "bg-purple-50",
      headerBg: "bg-purple-100",
      text: "text-purple-900",
      icon: "text-purple-600",
      btn: "bg-purple-600 hover:bg-purple-700",
      ring: "focus:ring-purple-400",
    },
  };

  const currentTheme = theme[color];

  return (
    <div
      className={`flex flex-col w-64 border rounded-lg shadow-sm bg-white z-10 transition-all duration-300 hover:shadow-md ${currentTheme.border} ${className}`}
    >
      <div
        className={`px-3 py-2 flex items-center justify-between border-b ${currentTheme.border} ${currentTheme.headerBg}`}
      >
        <div
          className={`flex items-center gap-2 font-bold ${currentTheme.text}`}
        >
          <Icon size={16} className={currentTheme.icon} />
          <span className="uppercase tracking-wide text-[10px]">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="bg-white/60 px-1.5 py-0.5 rounded-full text-[9px]  font-bold text-slate-600">
            {items.length}
          </span>
          {!readOnly && (
            <button
              onClick={() => (onManage ? onManage() : setIsAdding(!isAdding))}
              className={`p-0.5 rounded hover:bg-white/50 transition-colors ${isAdding ? "bg-white/50" : ""}`}
              title={onManage ? `Manage ${title.toLowerCase()}` : "Add New"}
              data-testid={`BTN_risk_canvas_list_${title.toLowerCase()}_add`}
            >
              <Plus size={14} className={currentTheme.text} />
            </button>
          )}
        </div>
      </div>

      {isAdding && !onManage && (
        <div className="p-2 border-b border-slate-100 bg-slate-50/50 animate-in slide-in-from-top-2 duration-200">
          <form onSubmit={handleAdd} className="flex gap-2" data-testid={`MOD_risk_canvas_list_${title.toLowerCase()}_inline_form`}>
            <input
              type="text"
              autoFocus
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder={placeholder}
              className={`flex-1 text-xs px-2 py-1 border border-slate-300 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${currentTheme.ring}`}
              data-testid={`INP_risk_canvas_list_${title.toLowerCase()}_new`}
            />
            <button
              type="submit"
              disabled={!newItem.trim()}
              className={`text-white px-2 py-1 rounded shadow-sm transition-colors text-[10px] font-bold uppercase tracking-wider ${currentTheme.btn} disabled:opacity-50`}
              data-testid={`BTN_risk_canvas_list_${title.toLowerCase()}_submit`}
            >
              Add
            </button>
          </form>
        </div>
      )}

      <div className="flex-1 p-2 min-h-[140px] max-h-[200px] overflow-y-auto custom-scrollbar">
        {items.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 text-[10px] italic p-4 text-center">
            Click + to add {title.toLowerCase()}.
          </div>
        ) : (
          <ul className="space-y-1">
            {items.map((item: any) => (
              <li
                key={item.id}
                className="group flex items-start gap-2 text-xs text-slate-600 bg-slate-50 hover:bg-slate-100 p-1.5 rounded border border-transparent hover:border-slate-200 transition-all"
              >
                <div
                  className={`mt-1 w-1 h-1 rounded-full shrink-0 ${currentTheme.bg.replace("bg-", "bg-slate-400")}`}
                />
                {onItemClick ? (
                  <button
                    type="button"
                    onClick={() => onItemClick(item.id)}
                    className={`flex-1 leading-relaxed text-left cursor-pointer hover:underline ${currentTheme.text}`}
                    title="Open details"
                  >
                    {item.text}
                  </button>
                ) : (
                  <span className="flex-1 leading-relaxed">{item.text}</span>
                )}
                {!readOnly && (
                  <button
                    onClick={() => onRemove(item.id)}
                    className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                    title="Remove"
                    data-testid={`BTN_risk_canvas_list_${title.toLowerCase()}_item_${item.id}_remove`}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// 2. The Central Risk Node (Circular)
const RiskNode = ({ data, residual, hasScore }: any) => {
  const getScoreColor = (score: number) => {
    if (score >= 15) return "status-badge xs danger";
    if (score >= 8) return "status-badge xs warning";
    return "status-badge xs success";
  };

  const inherent = data.likelihood * data.severity;
  // Show residual when we have a real score; else fall back to inherent.
  const primary = hasScore ? residual : inherent;
  const primaryLabel = hasScore ? "Residual" : "Risk Score";

  return (
    <div
      className="z-20 w-44 h-44 rounded-full bg-white border-[5px] border-slate-100 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center p-4 text-center group relative"
      data-testid="CARD_risk_canvas_node"
    >
      <div className="bg-red-50 p-2 rounded-full mb-1 ring-4 ring-red-50/50">
        <AlertTriangle size={18} className="text-red-500" />
      </div>

      <h2 className="text-xs font-bold text-slate-800 leading-snug line-clamp-3 w-32">
        {data.name}
      </h2>

      <div className="mt-2 flex flex-col items-center">
        <div className={getScoreColor(primary)}>
          {primary}
        </div>
        <span className="text-[8px] text-slate-400 uppercase tracking-widest mt-1 font-bold">
          {primaryLabel}
        </span>
        {hasScore && (
          <span className="text-[8px] text-slate-400 mt-0.5 ">
            Inherent {inherent}
          </span>
        )}
      </div>
    </div>
  );
};

// 3. Right Sidebar (Legend & History)
const RightSidebar = ({
  history,
  assessment,
  onAddScore,
  onDeleteScore,
  hasRiskId,
  canModify,
}: any) => {
  // Score button is gated by the SAME right as creating a risk — whoever can
  // create a risk holds the right to record its score events. Checked inline
  // (not via the canModify prop) so the gate can't be lost in the prop chain.
  const { check } = usePermissions();
  const canScore = check("RSK_RISK_REGISTER", "view+modify").allowed;

  const getScoreColor = (score: number) => {
    if (score >= 15) return "status-badge xs danger";
    if (score >= 8) return "status-badge xs warning";
    return "status-badge xs success";
  };

  return (
    <div className="w-80 shrink-0 flex flex-col gap-4 border-l border-slate-200 p-6 bg-slate-50/50 h-full overflow-hidden">
      {/* Legend Block */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
          <Calendar size={12} /> Assessment Details
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-500">Date</span>
            <span className=" font-medium text-slate-700">
              {assessment.date}
            </span>
          </div>
          <div className="h-px bg-slate-100" />
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-50 p-2 rounded border border-slate-100 text-center">
              <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">
                Inherent
              </div>
              <div className="text-lg font-black text-slate-700">
                {assessment.inherent}
              </div>
            </div>
            <div className="bg-slate-50 p-2 rounded border border-slate-100 text-center">
              <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">
                Residual
              </div>
              <div className="text-lg font-black text-slate-700">
                {assessment.residual}
              </div>
            </div>
          </div>
          <div className="status-badge xs success flex justify-between items-center">
            <span className="font-medium">Control Effectiveness</span>
            <span className="font-bold">{assessment.controlEffectiveness}</span>
          </div>
        </div>
      </div>

      {/* History Log */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col min-h-0">
        <div className="flex justify-between items-center mb-4 shrink-0">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <History size={12} /> Risk Journey
          </h3>
          {hasRiskId && (
            <button
              onClick={() => {
                if (!canScore) { swalError('You do not have permission to record a score for this risk.'); return; }
                onAddScore();
              }}
              className={`text-[10px] font-bold uppercase tracking-wider bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors${canScore ? ' hover:bg-blue-700' : ' opacity-50 cursor-not-allowed'}`}
              title={canScore ? "Record new score" : "Not authorised"}
              data-testid="BTN_risk_canvas_score_new"
            >
              <Plus size={12} /> New
            </button>
          )}
        </div>

        <div className="relative pl-2 flex-1 overflow-y-auto custom-scrollbar pr-2 min-h-0">
          {history.length === 0 && (
            <div className="text-[11px] italic text-slate-400 text-center py-6">
              {hasRiskId
                ? 'No score events yet. Click "+ New" to record one.'
                : "Open from a risk to record scores."}
            </div>
          )}
          {/* Vertical Connecting Line */}
          {history.length > 0 && (
            <div className="absolute left-[19px] top-2 bottom-4 w-0.5 bg-slate-200" />
          )}

          <div className="space-y-6 relative">
            {history.map((entry: any, index: number) => (
              <div
                key={entry.id ?? index}
                className="flex gap-4 relative group"
              >
                {/* Bubble */}
                <div className={getScoreColor(entry.score)}>
                  {entry.score}
                </div>

                {/* Content */}
                <div className="flex-1 pt-1 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-xs font-bold text-slate-700">
                      {entry.date}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-slate-400 ">
                        {entry.change}
                      </span>
                      {entry.id && onDeleteScore && (
                        <button
                          onClick={() => {
                            if (!canModify) { swalError('You do not have permission to modify this record.'); return; }
                            onDeleteScore(entry.id);
                          }}
                          className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                          title={canModify ? "Delete score event" : "Not authorised"}
                          data-testid={`BTN_risk_canvas_score_${entry.id}_delete`}
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {entry.program ? `Program: ${entry.program}` : "Adhoc"}
                    {entry.scorer ? ` · ${entry.scorer}` : ""}
                  </div>
                  {entry.note && (
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                      {entry.note}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// 4. SVG Connectors
const Connectors = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none z-0 hidden md:block overflow-visible"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
  >
    {/* LINES ONLY */}
    {/* Amber: TL -> Center Top */}
    <path
      d="M 32 20 L 50 20 L 50 37"
      stroke="#fbbf24"
      strokeWidth="1"
      fill="none"
      opacity="0.8"
      strokeLinejoin="round"
    />
    {/* Blue: TR -> Center Right */}
    <path
      d="M 80 28 L 80 50 L 61 50"
      stroke="#60a5fa"
      strokeWidth="1"
      fill="none"
      opacity="0.8"
      strokeLinejoin="round"
    />
    {/* Green: BL -> Center Left */}
    <path
      d="M 20 72 L 20 50 L 39 50"
      stroke="#34d399"
      strokeWidth="1"
      fill="none"
      opacity="0.8"
      strokeLinejoin="round"
    />
    {/* Purple: BR -> Center Bottom */}
    <path
      d="M 50 63 L 50 80 L 68 80"
      stroke="#c084fc"
      strokeWidth="1"
      fill="none"
      opacity="0.8"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Main App ---

interface RiskVisualizationProps {
  risk?: any;
  onClose?: () => void;
  canModify?: boolean;
}

export default function RiskVisualization({
  risk,
  onClose,
  canModify = false,
}: RiskVisualizationProps) {
  const riskDbId: number | undefined = risk?.id;
  const [riskData, setRiskData] = useState({
    id: risk?.risk_code || "R-102",
    name: risk?.risk_name || "Data Breach via 3rd Party Vendor",
    // No hardcoded L/S — until a score event exists the node must read 0, matching
    // the 0/0 assessment card. (Old 3×5 default surfaced a phantom Risk Score of 15.)
    likelihood: 0,
    severity: 0,
  });

  const [scores, setScores] = useState<RiskScore[]>([]);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);

  const loadScores = useCallback(async () => {
    if (!riskDbId) return;
    try {
      const data = await riskScoresService.listForRisk(riskDbId);
      setScores(data);
    } catch {
      setScores([]);
    }
  }, [riskDbId]);

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  // Latest score drives the central node L/S and the assessment summary card.
  useEffect(() => {
    if (scores.length === 0) return;
    const latest = scores[0]; // service returns newest-first
    setRiskData((prev) => ({
      ...prev,
      likelihood: latest.likelihood,
      severity: latest.severity,
    }));
  }, [scores]);

  const { formatDate } = useEntitySettings();

  const latest = scores[0];
  const ceLabel = (ce?: number, cef?: number) => {
    if (ce == null || cef == null) return "—";
    const avg = (ce + cef) / 2;
    if (avg >= 4) return "Strong";
    if (avg >= 3) return "Partial";
    if (avg >= 2) return "Weak";
    return "Minimal";
  };

  const assessmentDetails = latest
    ? {
        date: formatDate(latest.date_of_test || latest.created_on),
        inherent: latest.inherent_risk,
        residual: latest.residual_risk,
        controlEffectiveness: ceLabel(
          latest.control_existence,
          latest.control_effectiveness,
        ),
      }
    : {
        date: "—",
        inherent: 0,
        residual: 0,
        controlEffectiveness: "—",
      };

  // Build journey entries with delta vs the previous (older) event.
  const history = scores.map((s, idx) => {
    const prev = scores[idx + 1]; // older
    const delta = prev ? s.residual_risk - prev.residual_risk : null;
    const change =
      delta == null ? "Initial" : delta > 0 ? `+${delta}` : `${delta}`;
    return {
      id: s.id,
      date: formatDate(s.date_of_test || s.created_on),
      score: s.residual_risk,
      change,
      program: s.program_code || null,
      scorer: s.created_by_name || null,
      note: s.notes || "",
    };
  });

  const handleDeleteScore = async (scoreId: number) => {
    const ok = await confirmDelete({ title: "Delete this score event?" });
    if (!ok) return;
    try {
      await riskScoresService.delete(scoreId);
      await loadScores();
    } catch (e) {
      swalError("Failed to delete score");
    }
  };

  // Real data: causes / impacts / controls linked to this risk via rsk_*_maps.
  // Processes are *derived* from controls via ctr_procedure_maps → gov_processes.
  const [causes, setCauses] = useState<LinkedItem[]>([]);
  const [impacts, setImpacts] = useState<LinkedItem[]>([]);
  const [controls, setControls] = useState<LinkedItem[]>([]);
  const [processes, setProcesses] = useState<
    Array<LinkedItem & { via_controls?: string }>
  >([]);
  const [configTab, setConfigTab] = useState<RiskLinkType | null>(null);
  const [selectedControl, setSelectedControl] = useState<ControlRegister | null>(
    null,
  );
  const [loadingControl, setLoadingControl] = useState(false);

  const handleOpenControl = async (controlId: number) => {
    if (loadingControl) return;
    setLoadingControl(true);
    try {
      const ctrl = await controlRegisterService.getById(controlId);
      setSelectedControl(ctrl);
    } catch (e) {
      swalError("Failed to load control details");
    } finally {
      setLoadingControl(false);
    }
  };

  const loadLinks = useCallback(async () => {
    if (!riskDbId) return;
    try {
      const [c, i, ctrl, p] = await Promise.all([
        linkService.listForRisk(riskDbId, "causes"),
        linkService.listForRisk(riskDbId, "impacts"),
        linkService.listForRisk(riskDbId, "controls"),
        linkService.listProcessesViaControls(riskDbId),
      ]);
      setCauses(c);
      setImpacts(i);
      setControls(ctrl);
      setProcesses(p);
    } catch {
      // leave whatever we had
    }
  }, [riskDbId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // Map LinkedItem -> ListNode item shape ({id, text}).
  const toListItems = (rows: LinkedItem[]) =>
    rows.map((r) => ({
      id: r.id,
      text: r.code ? `${r.code} — ${r.name}` : r.name,
    }));

  // Inline remove: PUT current ids minus the removed one.
  const handleLinkRemove = async (kind: RiskLinkType, removedId: number) => {
    if (!riskDbId) return;
    if (!canModify) { swalError('You do not have permission to modify this record.'); return; }
    const currentRows =
      kind === "causes" ? causes : kind === "impacts" ? impacts : controls;
    const nextIds = currentRows
      .filter((r) => r.id !== removedId)
      .map((r) => r.id);
    try {
      await linkService.replaceForRisk(riskDbId, kind, nextIds);
      await loadLinks();
    } catch {
      swalError(`Failed to remove ${kind.slice(0, -1)}`);
    }
  };

  // Processes are derived (read-only). The ListNode still needs onAdd/onRemove
  // signatures, but they're no-ops and the "+" button is hidden via onManage.
  const noop = () => {};

  return (
    <div className="bg-white  text-slate-800 overflow-hidden flex flex-col h-full">
      {/* Top Header */}
      <div className="bg-white border-b border-slate-100 p-4 flex justify-between items-center z-20 relative">
        <div>
          <h1 className="text-xl font-black text-slate-800 tracking-tight">
            RISK FLOW CANVAS
          </h1>
          <p className="text-slate-400 text-[10px] font-medium uppercase tracking-widest mt-0.5">
            Bow-Tie Analysis Visualization
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Latest score summary */}
          {latest ? (
            <div className="hidden md:flex items-stretch gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
              <div className="flex flex-col items-center px-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest">
                  Inherent
                </span>
                <span className="text-sm font-black text-slate-700 leading-tight">
                  {latest.inherent_risk}
                </span>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="flex flex-col items-center px-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest">
                  Residual
                </span>
                <span
                  className={`text-sm font-black leading-tight ${
                    latest.residual_risk >= 15
                      ? "text-red-600"
                      : latest.residual_risk >= 8
                        ? "text-orange-600"
                        : "text-emerald-600"
                  }`}
                >
                  {latest.residual_risk}
                </span>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="flex flex-col items-start px-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest">
                  As of
                </span>
                <span className="text-[10px]  text-slate-600 leading-tight">
                  {formatDate(latest.date_of_test || latest.created_on)}
                </span>
              </div>
              {latest.created_by_name && (
                <>
                  <div className="w-px bg-slate-200" />
                  <div className="flex flex-col items-start px-2">
                    <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest">
                      By
                    </span>
                    <span className="text-[10px] text-slate-600 leading-tight">
                      {latest.created_by_name}
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="hidden md:flex gap-4 text-[9px] text-slate-400 ">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400"></div>{" "}
                CAUSE
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>{" "}
                PROCESS
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>{" "}
                CONTROL
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>{" "}
                IMPACT
              </div>
            </div>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="BTN_risk_canvas_close"
            >
              <X size={20} className="text-slate-500" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-row overflow-hidden h-full">
        {/* LEFT: Diagram Canvas */}
        <div className="flex-1 relative bg-white p-4 overflow-y-auto h-full min-w-0">
          <div className="w-full h-full max-w-4xl mx-auto flex flex-col justify-center min-h-[800px]">
            <div className="relative w-full aspect-square max-h-[800px] mx-auto">
              <Connectors />

              <div className="absolute inset-0 md:grid md:grid-cols-3 md:grid-rows-3 p-4 gap-4">
                {/* 1. TOP LEFT: CAUSES (Amber) */}
                <div className="md:col-start-1 md:row-start-1 flex justify-center md:justify-start md:items-start z-10">
                  <ListNode
                    title="Causes"
                    icon={Zap}
                    color="amber"
                    items={toListItems(causes)}
                    onAdd={() => {
                      /* managed via modal */
                    }}
                    onRemove={(id: number) => handleLinkRemove("causes", id)}
                    placeholder="New cause..."
                    onManage={
                      riskDbId ? () => setConfigTab("causes") : undefined
                    }
                  />
                </div>

                {/* 2. TOP RIGHT: PROCESSES (Blue) — derived from controls (read-only) */}
                <div className="md:col-start-3 md:row-start-1 flex justify-center md:justify-end md:items-start z-10">
                  <ListNode
                    title="Processes"
                    icon={Activity}
                    color="blue"
                    items={processes.map((p) => ({
                      id: p.id,
                      text: p.via_controls
                        ? `${p.name} (via ${p.via_controls})`
                        : p.name,
                    }))}
                    onAdd={noop}
                    onRemove={noop}
                    placeholder="Derived from controls"
                    readOnly
                  />
                </div>

                {/* 3. CENTER: Risk Node */}
                <div className="md:col-start-2 md:row-start-2 flex justify-center items-center z-20 pointer-events-none">
                  <div className="pointer-events-auto">
                    <RiskNode
                      data={riskData}
                      residual={latest?.residual_risk ?? 0}
                      hasScore={!!latest}
                    />
                  </div>
                </div>

                {/* 4. BOTTOM LEFT: CONTROLS (Green) */}
                <div className="md:col-start-1 md:row-start-3 flex justify-center md:justify-start md:items-end z-10">
                  <ListNode
                    title="Controls"
                    icon={Shield}
                    color="green"
                    items={toListItems(controls)}
                    onAdd={() => {
                      /* managed via modal */
                    }}
                    onRemove={(id: number) => handleLinkRemove("controls", id)}
                    placeholder="New control..."
                    onManage={
                      riskDbId ? () => setConfigTab("controls") : undefined
                    }
                    onItemClick={handleOpenControl}
                  />
                </div>

                {/* 5. BOTTOM RIGHT: IMPACTS (Purple) */}
                <div className="md:col-start-3 md:row-start-3 flex justify-center md:justify-end md:items-end z-10">
                  <ListNode
                    title="Impacts"
                    icon={TrendingUp}
                    color="purple"
                    items={toListItems(impacts)}
                    onAdd={() => {
                      /* managed via modal */
                    }}
                    onRemove={(id: number) => handleLinkRemove("impacts", id)}
                    placeholder="New impact..."
                    onManage={
                      riskDbId ? () => setConfigTab("impacts") : undefined
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Sidebar */}
        <RightSidebar
          history={history}
          assessment={assessmentDetails}
          hasRiskId={!!riskDbId}
          onAddScore={() => setScoreModalOpen(true)}
          onDeleteScore={handleDeleteScore}
          canModify={canModify}
        />
      </div>

      {riskDbId && (
        <ScoreRiskModal
          isOpen={scoreModalOpen}
          onClose={() => setScoreModalOpen(false)}
          riskId={riskDbId}
          riskName={risk?.risk_name}
          onSaved={loadScores}
        />
      )}

      {selectedControl && (
        <ControlConfigModal
          isOpen={!!selectedControl}
          onClose={() => setSelectedControl(null)}
          control={selectedControl}
          onSaved={() => {
            loadLinks();
          }}
        />
      )}

      {riskDbId && configTab && (
        <RiskConfigModal
          isOpen={!!configTab}
          onClose={() => {
            setConfigTab(null);
            loadLinks();
          }}
          risk={{
            id: riskDbId,
            risk_code: risk?.risk_code || "",
            risk_name: risk?.risk_name || "",
          }}
          initialTab={configTab}
        />
      )}
    </div>
  );
}
